// The Progress page's figures (#203), from GET /api/sessions/analysis rows:
//   { id, started_at, workout_type, duration_minutes,
//     devices: telemetry_summary.devices (w_per_beat, drift_pct, duration_s…),
//     power_curves: { indoor_bike?: { "5": W, … }, rower?: … },
//     np: { indoor_bike?: W }, hr_recovery: { bpm, count } }
// The server works those out from each recording (api/lib/sessionAnalysis).
// Pure, so it's tested without the page.

export const DURATIONS = [5, 15, 30, 60, 120, 300, 600, 1200, 1800, 3600];
export const MACHINES = [
  ["indoor_bike", "Bike"],
  ["rower", "Rower"],
];
export const RECENT_DAYS = 56; // "last 8 weeks" on the power curve
export const NEW_BEST_DAYS = 14; // a best this recent is flagged new

// FTP (Allen and Coggan): 95% of your best 20 minutes, from rides in the
// last 90 days so it follows your current form.
export const FTP_DAYS = 90;
export const FTP_SHARE = 0.95;

const DAY_MS = 24 * 60 * 60 * 1000;
const at = (row) => new Date(row?.started_at).getTime();
const valid = (rows) => (Array.isArray(rows) ? rows : []).filter((r) => Number.isFinite(at(r)));

export const durationLabel = (s) => (s < 60 ? `${s} s` : s < 3600 ? `${s / 60} min` : `${s / 3600} h`);

/** Machines that have a power curve in any session, in MACHINES order. */
export const machinesWithPower = (rows) =>
  MACHINES.filter(([dev]) => valid(rows).some((r) => r.power_curves?.[dev]));

/**
 * Best power for each duration on one machine: over the last RECENT_DAYS and
 * all time, each { watts, id, at }. `newBests` are durations whose all-time
 * best came in the last NEW_BEST_DAYS. Null without a curve.
 */
export const powerCurve = (rows, dev, { now = Date.now() } = {}) => {
  const list = valid(rows).filter((r) => r.power_curves?.[dev]);
  if (!list.length) return null;
  const recent = {};
  const allTime = {};
  for (const row of list) {
    const t = at(row);
    for (const d of DURATIONS) {
      const watts = Number(row.power_curves[dev][d]);
      if (!(watts > 0)) continue;
      const entry = { watts, id: row.id, at: t };
      if (!allTime[d] || watts > allTime[d].watts) allTime[d] = entry;
      if (now - t <= RECENT_DAYS * DAY_MS && (!recent[d] || watts > recent[d].watts)) recent[d] = entry;
    }
  }
  const newBests = DURATIONS.filter((d) => allTime[d] && now - allTime[d].at <= NEW_BEST_DAYS * DAY_MS);
  return { recent, allTime, newBests };
};

const bestTwenty = (rows, from, to) =>
  valid(rows)
    .filter((r) => at(r) > from && at(r) <= to)
    .reduce((best, r) => Math.max(best, Number(r.power_curves?.indoor_bike?.[1200]) || 0), 0);

/**
 * FTP from the bike: { watts, change } where change compares it with the
 * estimate 30 days ago (null when there wasn't one). Null without a 20 minute
 * effort in the last FTP_DAYS.
 */
export const estimateFtp = (rows, { now = Date.now() } = {}) => {
  const best = bestTwenty(rows, now - FTP_DAYS * DAY_MS, now);
  if (!best) return null;
  const then = now - 30 * DAY_MS;
  const before = bestTwenty(rows, then - FTP_DAYS * DAY_MS, then);
  const watts = Math.round(best * FTP_SHARE);
  return { watts, change: before ? watts - Math.round(before * FTP_SHARE) : null };
};

/**
 * Intensity Factor and Training Stress Score for one ride: IF = NP / FTP, and
 * TSS = hours × IF² × 100, so an hour at FTP is 100. Null without NP.
 */
export const rideStress = (row, ftp) => {
  const np = Number(row?.np?.indoor_bike);
  const seconds = Number(row?.devices?.indoor_bike?.duration_s);
  if (!(np > 0) || !(seconds > 0) || !(ftp > 0)) return null;
  const intensity = np / ftp;
  return {
    np,
    if: Math.round(intensity * 100) / 100,
    tss: Math.round((seconds / 3600) * intensity * intensity * 100),
  };
};

/** The last `count` rides with IF and TSS, newest first. */
export const recentRides = (rows, ftp, count = 4) =>
  valid(rows)
    .map((row) => ({ row, stress: rideStress(row, ftp) }))
    .filter((r) => r.stress)
    .sort((a, b) => at(b.row) - at(a.row))
    .slice(0, count);

/**
 * Points for the trend lines, oldest first: watts per beat and aerobic drift
 * on one machine (drift is only worked out for 20+ min steady efforts, so
 * watts per beat follows the same rides to compare like with like), and
 * heart-rate recovery after hard intervals.
 */
export const trends = (rows, dev = "indoor_bike") => {
  const list = valid(rows).sort((a, b) => at(a) - at(b));
  const steady = list.filter((r) => Number.isFinite(Number(r.devices?.[dev]?.drift_pct)));
  return {
    wPerBeat: steady
      .filter((r) => r.devices[dev].w_per_beat > 0)
      .map((r) => ({ at: at(r), value: Number(r.devices[dev].w_per_beat) })),
    drift: steady.map((r) => ({ at: at(r), value: Number(r.devices[dev].drift_pct) })),
    recovery: list
      .filter((r) => r.hr_recovery?.bpm > 0)
      .map((r) => ({ at: at(r), value: Number(r.hr_recovery.bpm) })),
  };
};

/**
 * Change from the first three points to the last three, as a % of the first
 * (null under four points, too few to call a trend).
 */
export const trendChange = (points) => {
  if (!points || points.length < 4) return null;
  const mean = (xs) => xs.reduce((sum, p) => sum + p.value, 0) / xs.length;
  const first = mean(points.slice(0, 3));
  const last = mean(points.slice(-3));
  return first ? Math.round(((last - first) / first) * 100) : null;
};
